import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter 
} from "@/components/ui/dialog";
import { Lock, AlertTriangle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useNotify } from "@/hooks/useNotify";

interface PasswordConfirmationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  title: string;
  description: string;
  actionLabel: string;
  variant?: "default" | "destructive";
}

const PasswordConfirmationDialog = ({ 
  isOpen, 
  onClose, 
  onSuccess, 
  title, 
  description, 
  actionLabel,
  variant = "default"
}: PasswordConfirmationDialogProps) => {
  const [password, setPassword] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [hasError, setHasError] = useState(false);
  const notify = useNotify();

  const handleVerifyPassword = async () => {
    if (!password.trim()) {
      setHasError(true);
      notify.error("Please enter your password to continue.");
      return;
    }

    setIsVerifying(true);
    setHasError(false);
    
    try {
      // Get current user's email for authentication
      const { data: { user: currentUser }, error: userError } = await supabase.auth.getUser();
      
      if (userError || !currentUser?.email) {
        setHasError(true);
        notify.error("Unable to verify user session. Please try logging in again.");
        return;
      }

      // Verify password by attempting to sign in
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: currentUser.email,
        password: password
      });

      if (signInError) {
        setHasError(true);
        notify.error("The password you entered is incorrect. Please try again.");
        return;
      }

      // Password is correct, proceed with the action
      setHasError(false);
      onSuccess();
      setPassword("");
      onClose();
      
    } catch (error: any) {
      setHasError(true);
      notify.error(error.message || "An unexpected error occurred. Please try again.");
    } finally {
      setIsVerifying(false);
    }
  };

  const handleClose = () => {
    setPassword("");
    setHasError(false);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Lock className="h-5 w-5" />
            {title}
          </DialogTitle>
          <DialogDescription className="flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 text-yellow-600 mt-0.5 flex-shrink-0" />
            <span>{description}</span>
          </DialogDescription>
        </DialogHeader>
        
        <div className="grid gap-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="admin-password">Confirm Your Password</Label>
            <Input
              id="admin-password"
              type="password"
              placeholder="Enter your admin password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                if (hasError) setHasError(false); // Clear error when user starts typing
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleVerifyPassword();
                }
              }}
              disabled={isVerifying}
              className={hasError ? "border-red-500 focus-visible:ring-red-500" : ""}
            />
            {hasError ? (
              <p className="text-xs text-red-600 font-medium">
                Incorrect password. Please try again.
              </p>
            ) : (
              <p className="text-xs text-muted-foreground">
                For security purposes, please re-enter your password to confirm this action.
              </p>
            )}
          </div>
        </div>
        
        <DialogFooter>
          <Button 
            variant="outline" 
            onClick={handleClose}
            disabled={isVerifying}
          >
            Cancel
          </Button>
          <Button 
            variant={variant}
            onClick={handleVerifyPassword}
            disabled={isVerifying || !password.trim()}
          >
            {isVerifying ? "Verifying..." : actionLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PasswordConfirmationDialog;